import { useLazyQuery, useQuery } from '@apollo/client';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';

import { ProductOffer, ProductOfferDocument, ProductOfferType } from '@savebetter/api';
import { SelectOption } from '@savebetter/components';

import { PRODUCT_DOCUMENT_QUERY, PRODUCT_OFFERS_TYPES_QUERY, PRODUCT_TABLE_OFFERS_QUERY } from '../graphql/query';

export interface ProductType {
  productCode: string;
  productType: string;
}
export interface FeaturedProductType {
  productId: string;
}

export const defaultProductTypeOptions: Array<SelectOption<ProductTableType>> = [
  { value: 'FEATURED_PRODUCTS', label: 'Featured Products' },
  { value: 'SAVINGS_ACCOUNT', label: 'Savings' },
  { value: 'HIGH_YIELD_CD', label: 'High-yield CDs' },
  { value: 'NO_PENALTY_CD', label: 'No penalty CDs' }
];
interface ProductOfferTypeQueryVars {
  filter: {
    productBankId?: string;
  };
}

export type ProductOfferTypeQueryItem = Pick<ProductOffer, 'type'>;
interface ProductOfferTypeQueryData {
  productOffers: {
    nodes: ProductOfferTypeQueryItem[];
  };
}

export const useProductOfferTypes = (productBankId: string) =>
  useQuery<ProductOfferTypeQueryData, ProductOfferTypeQueryVars>(PRODUCT_OFFERS_TYPES_QUERY, {
    variables: {
      filter: pickBy({ productBankId: productBankId }, identity)
    }
  });

export type ProductOfferQueryItem = Pick<
  ProductOffer,
  'id' | 'type' | 'name' | 'apy' | 'minAmount' | 'balanceComputation' | 'interestPosted' | 'maturity'
> & {
  productBank: Pick<ProductOffer['productBank'], 'id' | 'name' | 'logoUrl'>;
};

interface ProductOfferQueryVars {
  filter: {
    type?: ProductOfferType;
    productBankId?: string;
  };
}
interface ProductOfferQueryData {
  productOffers: {
    nodes: ProductOfferQueryItem[];
  };
}

export type ProductTableType = 'FEATURED_PRODUCTS' | ProductOfferType;

interface useProductOffersProps {
  currentProductType: ProductTableType;
  productBankId: string;
}
export const useProductOffers = ({ currentProductType, productBankId }: useProductOffersProps) =>
  useQuery<ProductOfferQueryData, ProductOfferQueryVars>(PRODUCT_TABLE_OFFERS_QUERY, {
    variables: {
      filter: pickBy(
        {
          type: currentProductType === 'FEATURED_PRODUCTS' ? undefined : [currentProductType],
          productBankId: productBankId
        },
        identity
      )
    }
  });

interface ProductOfferDocumentVars {
  productId: string;
}

interface ProductOfferDocumentData {
  productOfferDocument: ProductOfferDocument | null;
}

export const useProductOfferDocument = () =>
  useLazyQuery<ProductOfferDocumentData, ProductOfferDocumentVars>(PRODUCT_DOCUMENT_QUERY);
